import { HttpEvent, HttpEventType } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { BxButtonComponent, BxFileUploadComponent, BxFileUploadStatus } from '@beyonnex/bx-angular-ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NotificationsService } from '../../../services/notifications/notifications.service';
import { calculatePercentage } from '../../../utils/utils';
import { QundisImportService } from './qundis-import.service';

@Component({
  selector: 'bxi-qundis-import',
  imports: [BxFileUploadComponent, TranslatePipe, BxButtonComponent],
  templateUrl: './qundis-import.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QundisImportComponent {
  private readonly qundisImportService = inject(QundisImportService);
  private readonly notifications = inject(NotificationsService);
  private readonly translate = inject(TranslateService);

  protected readonly filesToUpload = signal<FileList | null>(null);
  protected readonly errorKey = signal<string | null>(null);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly uploading = signal<boolean>(false);
  private readonly _uploadStatus = signal<BxFileUploadStatus>({});

  protected readonly maxFileSizeMb = QundisImportService.MAX_FILE_SIZE_BYTES / 1_000_000;

  private readonly oversizedFiles = computed(() =>
    Array.from(this.filesToUpload() ?? []).filter((file) => file.size > QundisImportService.MAX_FILE_SIZE_BYTES),
  );

  protected readonly uploadDisabled = computed(() => this.uploading() || this.oversizedFiles().length > 0);

  protected readonly fileUploadStatus = computed<BxFileUploadStatus>(() => {
    const errorMessage = this.translate.instant('elsImport.errors.fileTooLarge', { limit: this.maxFileSizeMb });

    return {
      ...this._uploadStatus(),
      ...Object.fromEntries(this.oversizedFiles().map((file) => [file.name, { errorMessage }])),
    };
  });

  protected onFileChanged(files: FileList | null): void {
    this.errorMessage.set(null);
    this._uploadStatus.set({});
    this.filesToUpload.set(files);
    this.errorKey.set(this.oversizedFiles().length ? 'elsImport.errors.fileSizeLimit' : null);
  }

  protected importFiles(fileList: FileList): void {
    this.uploading.set(true);
    if (!fileList.length) {
      this.uploading.set(false);
      return;
    }
    const files = Array.from(fileList);
    files.forEach((file) => this.importFile(file));
  }

  private importFile(file: File): void {
    this.qundisImportService.uploadElsFile(file).subscribe({
      next: (event: HttpEvent<unknown>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            this._uploadStatus.update((prev) => ({
              ...prev,
              [file.name]: { progress: calculatePercentage(event.loaded, event.total ?? 1) },
            }));
            break;
          case HttpEventType.Response:
            if (event.status === 200) {
              this.filesToUpload.set(null);
              this.notifications.addSuccessNotification(
                this.translate.instant('system.info.importSuccess'),
                this.translate.instant('system.info.elsUpload', { fileName: file.name }),
              );
            } else {
              this._uploadStatus.update((prev) => ({
                ...prev,
                [file.name]: { errorMessage: this.translate.instant('system.errors.elsUpload') },
              }));
              this.errorKey.set('system.errors.elsUpload');
            }
            break;
        }
      },
      error: (err) => {
        console.error(err.message);
        this.errorKey.set('system.errors.elsUpload');
        this.errorMessage.set(err.error?.message ?? null);
        this.uploading.set(false);
      },
      complete: () => {
        this.uploading.set(false);
      },
    });
  }
}
